import type React from 'react';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useDeepCompareMemo } from '@hooks/use-deep-compare-memo';
import type { BlockVariation } from '@root/@types/database';
import { DEFAULT_VARIATION_ID } from '@root/helpers/variations';
import { pick } from 'lodash-es';
import { v4 as uuid } from 'uuid';

import { DUPLICATE_VARIATION_KEYS, NEW_VARIATION_KEYS } from './constants';
import type { VariationsTabsContextValue } from './context';
import { VariationsTabsContext } from './context';
import { useVariationInView } from './use-variation-read-model';

interface VariationsProviderProps {
  children?: React.ReactNode;
}

export const VariationsProvider = ({ children }: VariationsProviderProps) => {
  const { formValues: block, setFormValues } = useBlockEditFormStore(({ formValues, setFormValues }) => ({
    formValues,
    setFormValues,
  }));

  const {
    currentVariationIdx,
    setCurrentIdx,
    setCurrent,
    variationInView,
    variationsCount,
    selectedVariationId,
    visibilityDetails,
    hasLogic,
    variations,
  } = useVariationInView(block);

  const select = (newVariationId: string) => {
    return setFormValues((current) => {
      const isAlreadySelected = newVariationId === current?.selectedVariationId;
      return { ...current, selectedVariationId: isAlreadySelected ? undefined : newVariationId };
    });
  };

  const generateNext = (
    source: BlockVariation,
    isFirst: boolean,
    order: number,
    duplicate?: boolean,
  ): BlockVariation => {
    const name = isFirst ? 'Default' : `Variant ${order}`;
    const id = isFirst ? DEFAULT_VARIATION_ID : uuid();

    return {
      id,
      name,
      content: '',
      title: '',
      ...pick(source, duplicate ? DUPLICATE_VARIATION_KEYS : NEW_VARIATION_KEYS),
    } as BlockVariation;
  };

  const create = (payload?: BlockVariation) => {
    const { variations: currentVariations = [], ...rest } = block ?? {};
    const isFirst = currentVariations?.length === 0;

    const source = payload ?? currentVariations?.[currentVariationIdx] ?? rest;

    const newVariations: BlockVariation[] = [...currentVariations];

    const order = currentVariations?.length;
    const nextVariation = generateNext(source as BlockVariation, isFirst, order, isFirst);

    newVariations.push(nextVariation);

    if (isFirst) {
      const secondVariation = generateNext(source as BlockVariation, false, 1);
      newVariations.push(secondVariation);
    }

    setFormValues((current) => ({
      ...current,
      variations: newVariations,
    }));

    setCurrentIdx(newVariations.length - 1);
  };

  const duplicate = (variationId: string) => {
    const source = variations?.find((v) => v.id === variationId);

    if (!source) return;

    const newVariations: BlockVariation[] = [...variations];
    const order = variations?.length;
    const nextVariation = generateNext(source, false, order, true);

    newVariations.push(nextVariation);

    setFormValues((current) => ({ ...current, variations: newVariations }));

    setCurrentIdx(newVariations.length - 1);
  };

  const remove = (id: string) => {
    if (!variations.length) {
      return;
    }

    const newVariations = [...variations].filter((v) => v.id !== id);

    const length = newVariations.length;

    if (length < 2) {
      removeAll();
      return;
    }

    setFormValues((current) => ({
      ...current,
      variations: newVariations,
    }));

    setCurrentIdx(length - 1);
  };

  const removeAll = () => {
    setFormValues((current) => {
      if (!current) return;
      return { ...current, variations: [] };
    });
    setCurrentIdx(0);
  };

  const contextValue: VariationsTabsContextValue = useDeepCompareMemo(
    () => ({
      variationsCount,
      currentVariationIdx,
      selectedVariationId,
      variations,
      blockName: block?.name,
      variationInView,
      visibilityDetails,
      hasLogic,
      removeAll,
      remove,
      duplicate,
      create,
      select,
      setCurrent,
    }),
    // eslint-disable-next-line -- Update selected variation only when switching between editing blocks
    [currentVariationIdx, selectedVariationId, variations, block],
  );

  return <VariationsTabsContext.Provider value={contextValue}>{children}</VariationsTabsContext.Provider>;
};
